import type { ReactNode } from 'react'

import { ParticleWordmark } from '@/components/atoms/particle-wordmark.client'
import { TITLE, TITLE_SCREEN } from '@/lib/typography'
import { cn } from '@/lib/utils'

type PageHeaderProps = {
  /**
   * The headline. Pass JSX so the accent half can be wrapped in
   * `ACCENT_TEXT` at the call site — where the sentence is, rather than
   * splitting the string here and guessing which clause carries the emphasis.
   */
  children: ReactNode
  /** Optional chip under the band — studio's "Wrocław", wynajem's delivery note. */
  note?: ReactNode
  /**
   * This band opens a page that has to fit ONE screen — an offer ladder is
   * underneath it and every pixel here is one the cards do not get. The
   * headline takes {@link TITLE_SCREEN} instead, which gives its desktop size
   * back on a short window.
   */
  fit?: boolean
  /**
   * What stands in the band's RIGHT column, in place of the wordmark.
   *
   * One page uses it: `/pl/wynajem` puts its search field there, because the
   * owner wanted the search at the very top and the top is where the mark was
   * assembling. A catalogue page trades the mark for the tool; the other six
   * keep it. Anything passed here gets the full column, so it can be a control
   * rather than a picture.
   */
  aside?: ReactNode
}

/**
 * The opening band of a service page.
 *
 * This markup existed five times — wynajem, grafika, montaż, produkcja,
 * studio — identical down to the `mr-28 mt-24` on the mark, and it had already
 * drifted: studio's headline was set at `3.4rem` where the other four used
 * `3.2rem`, which is exactly the kind of difference nobody chooses and nobody
 * notices until the pages sit side by side.
 *
 * There is no kicker. Every page carried one — "PRODUKCJA" over "Wybierz…",
 * "GRAFIKA" over "Miniatury…" — a small uppercase label restating the word the
 * nav had just been clicked on and the headline was about to say again. It
 * bought a line of vertical space and no information.
 *
 * ## It is a MASTHEAD now, not a stack
 *
 * Before: a headline, a sentence and some chips run down the left edge of a
 * 1760px page, with 128px of clearance above them and a chrome logo parked in
 * the far corner. Nothing framed it, so the band had no top and no bottom — it
 * was a paragraph that happened to be first, and every service page opened
 * with the same one.
 *
 * Three things frame it now, and each is doing a job rather than decorating:
 *
 * **The rule above.** A hairline across the full container with a short brand
 * segment at its left end. It gives the band an edge to start from, and it puts
 * the gradient somewhere it can be a MARK — a deliberate 4rem of it — instead
 * of being poured into a whole clause on six pages in a row.
 *
 * **Two columns from `lg`.** Headline left at 7/12, the mark right at 5/12,
 * and `items-end` lands both columns on one bottom edge. The long headlines
 * break between their two clauses rather than mid-clause, which is how they
 * were written, and the band reads as composed rather than as
 * left-aligned-by-default. Below `lg` it is a stack, because at that width two
 * columns would be two narrow ones.
 *
 * **The accent is the gradient, and nothing else.** `ACCENT_TEXT` in
 * `lib/typography.ts` — the call sites wrap the clause, this component only
 * gives it room. The clause used to change TYPEFACE here as well; one headline
 * in two fonts is a seam, not an accent.
 *
 * ## THE MARK IS BACK, AND THE LEAD IS GONE
 *
 * Owner's call, made with the history in front of him. The right column used to
 * hold one paragraph per page — *„Sprzęt, na którym pracujemy — na Twój dzień
 * zdjęciowy."* and six more like it. It holds `ParticleWordmark` now: the FLOU
 * outlines assembling out of a dust cloud as the band is reached.
 *
 * Read the paragraph above this one before changing it back. `PageHeaderMark`
 * rendered this same wordmark in three.js beside this same headline and was
 * removed BECAUSE it repeated the mark in the fixed header 40px above it. That
 * argument has not gone away; it was weighed and overruled, and the difference
 * this time is that the mark arrives rather than sits — it is a moment on the
 * page, not a second logo printed on it.
 *
 * What it costs, stated plainly: seven pages lost a sentence of body copy that
 * a search engine could read. The strings are in git, not deleted from the
 * record — `git log -p` on this file and its call sites has every one of them.
 *
 * The three.js version stays gone. This mark is a 2D canvas that samples
 * `lib/logo-svg.ts`, so it costs no WebGL context and no geometry library.
 */
export function PageHeader({ children, note, fit = false, aside }: PageHeaderProps) {
  return (
    <div>
      {/* The band's top edge. `aria-hidden` on the rule itself rather than on a
          wrapper: it is one line of colour and carries no name. */}
      <div aria-hidden="true" className="relative h-px w-full bg-white/10">
        <span
          className="absolute inset-y-0 left-0 w-16 sm:w-24"
          style={{ background: 'var(--flou-gradient-solid)' }}
        />
      </div>

      {/* The air under the rule, and the mark's size, both give way on a short
          screen — an offer page has to fit its ladder under this band (see
          `lib/tier-fit.ts`), and 40px of nothing plus a 288px wordmark is 120px
          of a 800px window spent before the page has said anything. Full size
          returns above ~1100px, where there is room for it. */}
      <div className="grid gap-x-12 gap-y-6 pt-[clamp(1.25rem,3svh,2.5rem)] lg:grid-cols-12 lg:items-end">
        <header className="lg:col-span-7">
          <h1 className={cn(fit ? TITLE_SCREEN : TITLE, 'text-white')}>{children}</h1>
        </header>

        {/* `lg:pb-1` lifts the mark off the headline's descender line so the
            two columns end together rather than the right one hanging a few
            pixels low. Flush RIGHT from `lg` — a mark belongs on the edge of
            the band, and ranged left inside its own column it floats in the
            middle of the page with white space either side. `lg:mr-5` is the
            20px the owner asked for off that edge — hard against the container
            the mark read as having been cropped by it rather than placed. */}
        {/* A FLEX row, and that is the whole reason this div exists. The mark
            renders as `inline-block`, and an auto margin does not push an
            inline-level box anywhere — `lg:ml-auto` on it resolved to zero and
            the wordmark sat marooned in the middle of its own column with the
            container's right edge still 100px away. `justify-end` is what
            actually lands it on the edge of the band. */}
        <div className="flex lg:col-span-5 lg:justify-end lg:pb-1">
          {aside ?? (
            <ParticleWordmark className="w-full max-w-[15rem] sm:max-w-[min(18rem,22svh)] lg:mr-5" />
          )}
        </div>
      </div>

      {/* The chips get their OWN row, under both columns.
          They used to hang below the lead, inside the right column, and that is
          what broke the band: bottom-aligning a one-line headline against a
          lead-plus-chips stack lands the lead above the headline's cap line and
          the chips beside it, so the two halves agree on nothing but their
          bottom edge. The chips are facts about the SERVICE — where you collect
          it, how it travels — not about the sentence above them, so a row of
          their own is also where they belong. Both columns are plain text now,
          and plain text bottom-aligns cleanly whether the headline runs to one
          line or two. */}
      {note ? <div className={fit ? 'mt-[clamp(0.75rem,2svh,1.5rem)]' : 'mt-6'}>{note}</div> : null}
    </div>
  )
}
